import { useCallback, useMemo } from 'react'
import { BranchMultiSelect, MultiSelectItem } from '~/components/repo-selection/BranchMultiSelect'
import { useBranchList } from '~/hooks/organization/useBranchList'
import { RepositoryBranches } from '~/types/ResponseType'

interface RepoComponentProps {
  repo: RepositoryBranches
  connType: string
  setRepos: React.Dispatch<React.SetStateAction<RepositoryBranches[]>>
}

function RepoComponent({ repo, connType, setRepos }: RepoComponentProps) {
  // Lazy load branches: only fetch when the user focuses/opens the dropdown (not on mount)
  const {
    branches: availableBranches,
    isLoading,
    loadIfNeeded
  } = useBranchList({
    conn_type: connType,
    full_name: repo.name,
    lazy: true
  })

  const handleSelectBranches = useCallback(
    (repoInfo: RepositoryBranches, selectedBranches: string[]) => {
      if (selectedBranches.length >= 0) {
        setRepos(prevRepos =>
          prevRepos.map(repos =>
            repos.name === repoInfo.name ? { ...repos, branches: selectedBranches } : repos
          )
        )
      }
    },
    [setRepos]
  )

  // Combine previously selected branches with newly loaded branches
  const allBranches = useMemo(() => {
    const combined = new Set([...repo.branches, ...availableBranches])
    return Array.from(combined).filter(
      branch => !branch.startsWith('curie-dev') && !branch.startsWith('curie_dev')
    )
  }, [repo.branches, availableBranches])

  // Convert branch data to MultiSelectItem format for BranchMultiSelect
  const branchItems: MultiSelectItem[] = useMemo(
    () =>
      allBranches.map(branch => ({
        id: branch,
        label: branch,
        value: branch
      })),
    [allBranches]
  )

  return (
    <div key={repo.name}>
      <div className='mb-6'>
        <div className='flex items-center gap-2 max-w-full sm:max-w-[448px] px-4'>
          <div className='text-[rgba(0,0,0,0.65)] font-mono text-sm font-semibold truncate'>
            {repo.name}
          </div>
          {repo.default_branch && (
            <span className='text-[11px] font-mono text-[rgba(0,0,0,0.35)] shrink-0'>
              default: {repo.default_branch}
            </span>
          )}
        </div>
        <div className='w-full sm:w-[448px]'>
          <BranchMultiSelect
            items={branchItems}
            selectedItems={repo.branches}
            onSelectionChange={selectedBranches => {
              handleSelectBranches(repo, selectedBranches)
            }}
            placeholder='Search or select branches'
            searchPlaceholder='Search or select branches'
            showSelectedAsChips={true}
            className='w-full sm:w-[448px]'
            maxHeight='max-h-64'
            isLoading={isLoading}
            onOpenChange={isOpen => {
              if (isOpen) loadIfNeeded()
            }}
          />
        </div>
      </div>
    </div>
  )
}

interface BranchSelectionFormProps {
  repos: RepositoryBranches[]
  setRepos: React.Dispatch<React.SetStateAction<RepositoryBranches[]>>
  serverName: string
}

export function BranchSelectionForm({ repos, setRepos, serverName }: BranchSelectionFormProps) {
  return (
    <>
      {repos.map((repo, index) => (
        <RepoComponent key={index} repo={repo} connType={serverName} setRepos={setRepos} />
      ))}
    </>
  )
}
